import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { emitFtueEvent } from './events';
import {
  dismissWhatsNew,
  FTUE_VERSION,
  loadFtuePersistedState,
  markTourCompleted,
  markTourProgress,
  markTourSkipped,
  markWelcomeShown,
  shouldAutoOpenWelcome,
  shouldShowWhatsNew,
} from './persistence';
import {
  getFtueJourney,
  getFtueTarget,
  getTourStepIndex,
  groupedTargetsForSurface,
  resolveActiveSurface,
  surfaceJourneyIdForSurface,
  targetsForSurface,
} from './registry';
import type {
  FtueJourneyId,
  FtueMode,
  FtueNavigateApi,
  FtuePhase,
  FtuePrepareAction,
  FtueReturnFrame,
  FtueSurfaceContext,
  FtueTarget,
  FtueTourStep,
  FtueUiSnapshot,
} from './types';

export type UseFtueControllerArgs = {
  ready: boolean;
  surfaceCtx: FtueSurfaceContext;
  nav: FtueNavigateApi;
};

export type FtueTourSource = 'auto' | 'help' | 'whats-new' | 'surface';

export type FtueController = {
  phase: FtuePhase;
  stepIndex: number;
  stepCount: number;
  activeTarget: FtueTarget | null;
  activeStep: FtueTourStep | null;
  journeyId: FtueJourneyId | null;
  journeyLabel: string | null;
  isDeepDive: boolean;
  canDeepDive: boolean;
  surfaceTargets: FtueTarget[];
  contentTargets: FtueTarget[];
  chromeTargets: FtueTarget[];
  activeSurface: ReturnType<typeof resolveActiveSurface>;
  canResume: boolean;
  surfaceTourAvailable: boolean;
  openHelp: () => void;
  startTour: (source: FtueTourSource, journeyId?: FtueJourneyId) => void;
  startSurfaceTour: () => void;
  startFullTour: (source?: FtueTourSource) => void;
  resumeTour: () => void;
  enterDeepDive: () => void;
  exitDeepDive: () => void;
  nextStep: () => void;
  prevStep: () => void;
  skipTour: () => void;
  completeTour: () => void;
  closeHelp: () => void;
  openCatalog: () => void;
  openContextual: () => void;
  focusTarget: (targetId: string) => void;
  dismissWhatsNewInvite: () => void;
  starterPrompt: string;
};

const STARTER_PROMPT =
  'What should I watch this period in the current scope?';

async function waitForAnchor(
  anchor: string,
  timeoutMs = 2500,
  isCancelled?: () => boolean,
): Promise<boolean> {
  if (typeof document === 'undefined' || !anchor) return false;
  const start = Date.now();
  while (Date.now() - start < timeoutMs) {
    if (isCancelled?.()) return false;
    let el: Element | null = null;
    try {
      el = document.querySelector(`[data-ftue="${CSS.escape(anchor)}"]`);
    } catch {
      el = document.querySelector(`[data-ftue="${anchor}"]`);
    }
    if (el instanceof HTMLElement) {
      const r = el.getBoundingClientRect();
      if (r.width > 1 && r.height > 1) return true;
    }
    await new Promise(r => setTimeout(r, 50));
  }
  return false;
}

/** Yield so React can commit setState from prepare actions before we poll. */
async function settleFrame(isCancelled?: () => boolean): Promise<void> {
  await new Promise<void>(resolve => {
    if (typeof requestAnimationFrame === 'function') {
      requestAnimationFrame(() => resolve());
    } else {
      setTimeout(resolve, 16);
    }
  });
  if (isCancelled?.()) return;
  await new Promise(r => setTimeout(r, 50));
}

function primaryModeForSurface(
  surface: ReturnType<typeof resolveActiveSurface>,
  fallback: FtueMode,
): FtueMode {
  if (surface === 'live' || surface === 'comparison-live') return 'live';
  if (surface === 'settings' || surface === 'comparison-settings') {
    return 'settings';
  }
  if (surface === 'horizon') return 'horizon';
  if (
    surface === 'performance' ||
    surface === 'comparison-perf' ||
    surface === 'perf-drill' ||
    surface === 'north-star' ||
    surface === 'reviews'
  ) {
    return 'performance';
  }
  return fallback;
}

export function useFtueController({
  ready,
  surfaceCtx,
  nav,
}: UseFtueControllerArgs): FtueController {
  const [phase, setPhase] = useState<FtuePhase>('idle');
  const [stepIndex, setStepIndex] = useState(0);
  const [journeyId, setJourneyId] = useState<FtueJourneyId | null>(null);
  const [spotlightTargetId, setSpotlightTargetId] = useState<string | null>(
    null,
  );
  const snapshotRef = useRef<FtueUiSnapshot | null>(null);
  const returnFrameRef = useRef<FtueReturnFrame | null>(null);
  const transitionGenRef = useRef(0);
  const navRef = useRef(nav);
  navRef.current = nav;
  const bootedRef = useRef(false);
  const journeyIdRef = useRef<FtueJourneyId | null>(null);
  journeyIdRef.current = journeyId;
  const stepIndexRef = useRef(0);
  stepIndexRef.current = stepIndex;

  const activeSurface = useMemo(
    () => resolveActiveSurface(surfaceCtx),
    [surfaceCtx],
  );

  const surfaceTargets = useMemo(
    () => targetsForSurface(activeSurface),
    [activeSurface],
  );

  const grouped = useMemo(
    () => groupedTargetsForSurface(activeSurface),
    [activeSurface],
  );

  const persisted = loadFtuePersistedState();
  const canResume =
    !!persisted.lastStepId &&
    persisted.completedVersion !== FTUE_VERSION &&
    (persisted.lastJourneyId == null || persisted.lastJourneyId === 'full');

  const activeJourney = journeyId ? getFtueJourney(journeyId) : undefined;
  const activeStep =
    phase === 'tour' && activeJourney
      ? (activeJourney.steps[stepIndex] ?? null)
      : null;

  const activeTarget = useMemo(() => {
    if (phase === 'tour' && activeStep) {
      return getFtueTarget(activeStep.targetId) ?? null;
    }
    if (spotlightTargetId) return getFtueTarget(spotlightTargetId) ?? null;
    return null;
  }, [phase, activeStep, spotlightTargetId]);

  const cancelTransitions = useCallback(() => {
    transitionGenRef.current += 1;
  }, []);

  const restoreIfNeeded = useCallback(() => {
    const snap = snapshotRef.current;
    snapshotRef.current = null;
    returnFrameRef.current = null;
    if (snap) navRef.current.restoreSnapshot(snap);
  }, []);

  const applyPrepare = useCallback(async (actions?: FtuePrepareAction[]) => {
    if (!actions?.length) return;
    const api = navRef.current;
    for (const a of actions) {
      if (a.kind === 'mode') api.setMode(a.mode);
      else if (a.kind === 'openPerfDrill') api.openPerfDrill(a.metricKey);
      else if (a.kind === 'closePerfDrill') api.closePerfDrill();
      else if (a.kind === 'openAgent') api.openAgent();
      else if (a.kind === 'closeAgent') api.closeAgent();
      else if (a.kind === 'closeOverlays') api.closeOverlays();
      else if (a.kind === 'clearComparison') api.clearComparison();
    }
    await new Promise(r => setTimeout(r, 80));
  }, []);

  const goToStep = useCallback(
    async (index: number, activeJourneyId: FtueJourneyId, depth = 0) => {
      const journey = getFtueJourney(activeJourneyId);
      if (!journey) return;
      if (depth > journey.steps.length) return;

      const gen = ++transitionGenRef.current;
      const isCancelled = () => gen !== transitionGenRef.current;

      const clamped = Math.max(0, Math.min(journey.steps.length - 1, index));
      const step = journey.steps[clamped];
      if (!step) return;

      setJourneyId(activeJourneyId);
      setStepIndex(clamped);
      await applyPrepare(step.prepare);
      if (isCancelled()) return;
      await settleFrame(isCancelled);
      if (isCancelled()) return;

      const target = getFtueTarget(step.targetId);
      if (target?.anchor) {
        const ok = await waitForAnchor(
          target.anchor,
          step.waitMs ?? 2500,
          isCancelled,
        );
        if (isCancelled()) return;
        if (!ok && !step.required && clamped < journey.steps.length - 1) {
          await goToStep(clamped + 1, activeJourneyId, depth + 1);
          return;
        }
      }
      if (isCancelled()) return;

      markTourProgress(step.id, activeJourneyId);
      emitFtueEvent({
        type: 'ftue_step_viewed',
        version: FTUE_VERSION,
        stepId: step.id,
        index: clamped,
        journeyId: activeJourneyId,
      });
    },
    [applyPrepare],
  );

  const beginGuided = useCallback(
    (
      source: FtueTourSource,
      nextJourneyId: FtueJourneyId,
      startAt = 0,
    ) => {
      if (!snapshotRef.current) {
        snapshotRef.current = navRef.current.captureSnapshot();
      }
      markWelcomeShown();
      cancelTransitions();
      returnFrameRef.current = null;
      setSpotlightTargetId(null);
      setPhase('tour');
      setJourneyId(nextJourneyId);
      emitFtueEvent({
        type: 'ftue_start',
        version: FTUE_VERSION,
        source,
        journeyId: nextJourneyId,
      });
      void goToStep(startAt, nextJourneyId);
    },
    [cancelTransitions, goToStep],
  );

  const startTour = useCallback(
    (source: FtueTourSource, nextJourneyId: FtueJourneyId = 'full') => {
      beginGuided(source, nextJourneyId, 0);
    },
    [beginGuided],
  );

  const startFullTour = useCallback(
    (source: FtueTourSource = 'help') => {
      beginGuided(source, 'full', 0);
    },
    [beginGuided],
  );

  const startSurfaceTour = useCallback(() => {
    const mode = primaryModeForSurface(activeSurface, surfaceCtx.mode);
    beginGuided(
      'surface',
      surfaceJourneyIdForSurface(activeSurface, mode),
      0,
    );
  }, [activeSurface, beginGuided, surfaceCtx.mode]);

  const resumeTour = useCallback(() => {
    const state = loadFtuePersistedState();
    const resumeJourney: FtueJourneyId =
      state.lastJourneyId === 'full' || state.lastJourneyId == null
        ? 'full'
        : 'full';
    const startAt = getTourStepIndex(state.lastStepId, resumeJourney);
    beginGuided('help', resumeJourney, startAt);
  }, [beginGuided]);

  const completeTour = useCallback(() => {
    cancelTransitions();
    const completedId = journeyIdRef.current ?? 'full';
    markTourCompleted(completedId);
    emitFtueEvent({
      type: 'ftue_complete',
      version: FTUE_VERSION,
      journeyId: completedId,
    });
    setPhase('idle');
    setJourneyId(null);
    setSpotlightTargetId(null);
    restoreIfNeeded();
  }, [cancelTransitions, restoreIfNeeded]);

  const skipTour = useCallback(() => {
    cancelTransitions();
    const activeId = journeyIdRef.current;
    const journey = activeId ? getFtueJourney(activeId) : undefined;
    const step = journey?.steps[stepIndexRef.current];
    markTourSkipped(step?.id ?? null, activeId);
    emitFtueEvent({
      type: 'ftue_skip',
      version: FTUE_VERSION,
      stepId: step?.id ?? null,
      index: stepIndexRef.current,
      journeyId: activeId,
    });
    setPhase('idle');
    setJourneyId(null);
    setSpotlightTargetId(null);
    restoreIfNeeded();
  }, [cancelTransitions, restoreIfNeeded]);

  const exitDeepDive = useCallback(() => {
    const frame = returnFrameRef.current;
    returnFrameRef.current = null;
    if (!frame) {
      setPhase('idle');
      setJourneyId(null);
      restoreIfNeeded();
      return;
    }
    emitFtueEvent({
      type: 'ftue_deep_dive_exit',
      journeyId: journeyIdRef.current ?? frame.journeyId,
    });
    setJourneyId(frame.journeyId);
    void goToStep(frame.stepIndex, frame.journeyId);
  }, [goToStep, restoreIfNeeded]);

  const enterDeepDive = useCallback(() => {
    const activeId = journeyIdRef.current;
    const journey = activeId ? getFtueJourney(activeId) : undefined;
    const step = journey?.steps[stepIndexRef.current];
    const deepId = step?.deepDiveJourneyId;
    if (!deepId || !getFtueJourney(deepId)) return;
    returnFrameRef.current = {
      journeyId: activeId!,
      stepIndex: stepIndexRef.current,
    };
    emitFtueEvent({
      type: 'ftue_deep_dive_enter',
      journeyId: deepId,
      fromStepId: step.id,
    });
    setJourneyId(deepId);
    void goToStep(0, deepId);
  }, [goToStep]);

  const nextStep = useCallback(() => {
    const activeId = journeyIdRef.current;
    const journey = activeId ? getFtueJourney(activeId) : undefined;
    if (!journey || !activeId) return;
    if (stepIndexRef.current >= journey.steps.length - 1) {
      if (journey.kind === 'deep' && returnFrameRef.current) {
        exitDeepDive();
        return;
      }
      completeTour();
      return;
    }
    void goToStep(stepIndexRef.current + 1, activeId);
  }, [completeTour, exitDeepDive, goToStep]);

  const prevStep = useCallback(() => {
    const activeId = journeyIdRef.current;
    const journey = activeId ? getFtueJourney(activeId) : undefined;
    if (!journey || !activeId) return;
    if (stepIndexRef.current <= 0) {
      if (journey.kind === 'deep' && returnFrameRef.current) {
        exitDeepDive();
      }
      return;
    }
    void goToStep(stepIndexRef.current - 1, activeId);
  }, [exitDeepDive, goToStep]);

  const closeHelp = useCallback(() => {
    if (phase === 'tour') {
      skipTour();
      return;
    }
    cancelTransitions();
    setPhase('idle');
    setSpotlightTargetId(null);
  }, [cancelTransitions, phase, skipTour]);

  const openHelp = useCallback(() => {
    emitFtueEvent({ type: 'ftue_help_open', surface: activeSurface });
    setSpotlightTargetId(null);
    setPhase('contextual');
  }, [activeSurface]);

  const openCatalog = useCallback(() => {
    emitFtueEvent({ type: 'ftue_catalog_open' });
    setSpotlightTargetId(null);
    setPhase('catalog');
  }, []);

  const openContextual = useCallback(() => {
    setSpotlightTargetId(null);
    setPhase('contextual');
  }, []);

  const focusTarget = useCallback((targetId: string) => {
    const t = getFtueTarget(targetId);
    if (!t) return;
    emitFtueEvent({
      type: 'ftue_help_target_open',
      targetId,
      surface: t.surface,
    });
    if (t.anchor) {
      // Catalog-only targets become live spotlights when their conditional
      // drawer, drill, or wizard anchor is currently mounted.
      let mounted = false;
      if (typeof document !== 'undefined') {
        try {
          const el = document.querySelector(
            `[data-ftue="${CSS.escape(t.anchor)}"]`,
          );
          mounted = el instanceof HTMLElement;
        } catch {
          mounted = !!document.querySelector(`[data-ftue="${t.anchor}"]`);
        }
      }
      if (!mounted) {
        setSpotlightTargetId(targetId);
        setPhase('catalog');
        return;
      }
      setSpotlightTargetId(targetId);
      setPhase('contextual');
      return;
    }
    setSpotlightTargetId(targetId);
    setPhase('catalog');
  }, []);

  const dismissWhatsNewInvite = useCallback(() => {
    dismissWhatsNew();
    emitFtueEvent({
      type: 'ftue_whats_new_dismiss',
      version: FTUE_VERSION,
    });
    setPhase('idle');
  }, []);

  useEffect(() => {
    if (!ready || bootedRef.current) return;
    bootedRef.current = true;
    const state = loadFtuePersistedState();
    if (shouldAutoOpenWelcome(state)) {
      emitFtueEvent({ type: 'ftue_welcome_shown', version: FTUE_VERSION });
      setPhase('welcome');
      return;
    }
    if (shouldShowWhatsNew(state)) {
      emitFtueEvent({ type: 'ftue_whats_new_shown', version: FTUE_VERSION });
      setPhase('whats-new');
    }
  }, [ready]);

  const isDeepDive = activeJourney?.kind === 'deep';
  const canDeepDive = !!activeStep?.deepDiveJourneyId && !isDeepDive;

  return {
    phase,
    stepIndex,
    stepCount: activeJourney?.steps.length ?? 0,
    activeTarget,
    activeStep,
    journeyId,
    journeyLabel: activeJourney?.label ?? null,
    isDeepDive,
    canDeepDive,
    surfaceTargets,
    contentTargets: grouped.content,
    chromeTargets: grouped.chrome,
    activeSurface,
    canResume,
    surfaceTourAvailable:
      activeSurface === 'live' ||
      activeSurface === 'performance' ||
      activeSurface === 'settings' ||
      activeSurface.startsWith('comparison-'),
    openHelp,
    startTour,
    startSurfaceTour,
    startFullTour,
    resumeTour,
    enterDeepDive,
    exitDeepDive,
    nextStep,
    prevStep,
    skipTour,
    completeTour,
    closeHelp,
    openCatalog,
    openContextual,
    focusTarget,
    dismissWhatsNewInvite,
    starterPrompt: STARTER_PROMPT,
  };
}
